/**
 * 路面预瞄 **3D 场景**前向展示距离（米），与设置项 `previewSceneDisplayRangeM` 一致。
 * HUD 雷达量程固定为 `PREVIEW_SCENE_HUD_RADAR_RANGE_M`，不随本项变化。
 */
export const PREVIEW_SCENE_DISPLAY_RANGE_OPTIONS = [
  5, 10, 15, 20, 25, 30,
] as const;

/** 预瞄页 HUD 雷达地图量程（米），与「路面预瞄展示距离」设置解耦 */
export const PREVIEW_SCENE_HUD_RADAR_RANGE_M = 15;

export type PreviewSceneDisplayRangeM =
  (typeof PREVIEW_SCENE_DISPLAY_RANGE_OPTIONS)[number];

/** 历史默认路宽（m），车道横向比例曾按 12m 路对齐 */
const PREVIEW_SCENE_LEGACY_ROAD_WIDTH_M = 12;

/**
 * 简模/命名基准车长（m，XZ 最大边），固定 3.5；场景里实际显示车长 =
 * `PREVIEW_SCENE_VEHICLE_TARGET_DEPTH_M`（基准 × `PREVIEW_SCENE_VEHICLE_DISPLAY_SCALE`）。
 */
export const PREVIEW_SCENE_VEHICLE_CHASSIS_DEPTH_M = 3.5;

/**
 * 3D 场景中相对基准车长的放大倍率（1 = 不放大）。
 * 调大则 GLB/简模纵向、轮迹横向、自车 group 的 z 平移同比变化。
 */
export const PREVIEW_SCENE_VEHICLE_DISPLAY_SCALE = 4.5 / 3.5;

/** 场景中 GLB/简模拟合使用的目标车长（m）= 基准 × 放大比例 */
export const PREVIEW_SCENE_VEHICLE_TARGET_DEPTH_M =
  PREVIEW_SCENE_VEHICLE_CHASSIS_DEPTH_M * PREVIEW_SCENE_VEHICLE_DISPLAY_SCALE;

/**
 * 沥青路面总宽（m），与 vehiclePerceptionScene 路面 Plane 一致；窄于历史 12m。
 */
export const PREVIEW_SCENE_ROAD_WIDTH_M = 8.5;

/** 路面半宽（m），用于障碍物横向钳位与车道换算 */
export const PREVIEW_SCENE_ROAD_HALF_WIDTH_M = PREVIEW_SCENE_ROAD_WIDTH_M / 2;

/** 相对历史 12m 路、±3m 车道的横向比例（车道/减速带落位） */
export const PREVIEW_SCENE_LANE_X_SCALE =
  PREVIEW_SCENE_ROAD_HALF_WIDTH_M / (PREVIEW_SCENE_LEGACY_ROAD_WIDTH_M / 2);

/** 轮迹/井盖横向：与 `PREVIEW_SCENE_VEHICLE_DISPLAY_SCALE` 一致（与 GLB fit 一致） */
export const PREVIEW_SCENE_TRACK_X_SCALE = PREVIEW_SCENE_VEHICLE_DISPLAY_SCALE;

/**
 * 自车 group 的世界 z：取正半长，沿世界 +z 平移半车身（观感为「往后」挪车；负半长会呈「往前」）。
 */
export const PREVIEW_SCENE_VEHICLE_GROUP_POSITION_Z_M =
  PREVIEW_SCENE_VEHICLE_TARGET_DEPTH_M / 2;

export const DEFAULT_PREVIEW_SCENE_DISPLAY_RANGE_M: PreviewSceneDisplayRangeM =
  5;

const OPTION_SET = new Set<number>(PREVIEW_SCENE_DISPLAY_RANGE_OPTIONS);

export function clampPreviewSceneDisplayRangeM (
  n: number
): PreviewSceneDisplayRangeM {
  if (Number.isFinite(n) && OPTION_SET.has(n)) {
    return n as PreviewSceneDisplayRangeM;
  }
  return DEFAULT_PREVIEW_SCENE_DISPLAY_RANGE_M;
}

export function parseStoredPreviewSceneDisplayRangeM (
  raw: string | null
): PreviewSceneDisplayRangeM {
  if (raw == null || raw === "") {
    return DEFAULT_PREVIEW_SCENE_DISPLAY_RANGE_M;
  }
  return clampPreviewSceneDisplayRangeM(Number(raw));
}

export function isPreviewSceneDisplayRangeM (
  n: number
): n is PreviewSceneDisplayRangeM {
  return OPTION_SET.has(n);
}
